import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Eye, Users, MousePointerClick, Lock, CreditCard, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Kpi, WindowSelector, DailyChart, SourcesTable, CampaignsTable, SOURCE_LABEL, SOURCE_KLASS } from "./builder/statsWidgets";
import { FunnelByDestination } from "./builder/FunnelByDestination";

const STATUS_LABEL = { draft: "Borrador", published: "Publicado", closed: "Cerrado" };
const STATUS_KLASS = { draft: "bg-clay-200 text-clay-800", published: "bg-pine/15 text-pine", closed: "bg-clay-500/15 text-clay-700" };
const fmtDate = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function CuratedStatsDashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/curated-trips/stats/overview", { params: { days } })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudieron cargar las estadísticas"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="px-8 py-8 max-w-7xl" data-testid="curated-stats-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="smallcaps">Curated Trips</div>
          <h1 className="font-serif text-5xl leading-none mt-3">Estadísticas</h1>
          <p className="text-sm text-clay-700 mt-3 max-w-2xl">
            Tráfico real del hub, las landings por destino y los itinerarios, y su conversión a reservas. Sin visitas del equipo ni robots.
          </p>
        </div>
        <WindowSelector days={days} onChange={setDays}/>
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-sm text-clay-600"><Loader2 size={14} className="animate-spin"/> Cargando…</div>
      ) : (
        <div className={`space-y-6 ${loading ? "opacity-60" : ""}`}>
          {data.views_total === 0 && (
            <div className="border border-dashed border-clay-300 p-8 text-center text-sm text-clay-600" data-testid="stats-empty">
              Todavía no hay visitas registradas en ningún viaje. Empezarán a contarse en cuanto alguien abra un enlace público.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi icon={Eye} label="Visitas" value={data.views} sub={`${data.devices.mobile} móvil · ${data.devices.desktop} escritorio`} testid="kpi-views"/>
            <Kpi icon={Users} label="Visitantes únicos" value={data.visitors} testid="kpi-visitors"/>
            <Kpi icon={MousePointerClick} label="Clic en Reserve" value={data.reserve_opens} sub={`${data.rates.reserve_open}% de las visitas`} testid="kpi-reserve"/>
            <Kpi icon={Lock} label="Plazas retenidas" value={data.holds} sub={`${data.rates.hold}% conversión a reserva`} testid="kpi-holds"/>
            <Kpi icon={CreditCard} label="Pagadas" value={data.paid} sub={`${data.seats_paid} plazas · ${data.rates.paid}% de visitas`} testid="kpi-paid"/>
          </div>

          <div className="border border-clay-300 bg-white" data-testid="stats-per-trip">
            <div className="smallcaps px-4 pt-4 pb-2">Por viaje ({data.trips.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="text-clay-500 text-left">
                  <tr>
                    <th className="px-4 py-1 font-medium">Viaje</th>
                    <th className="px-4 py-1 font-medium">Estado</th>
                    <th className="px-4 py-1 font-medium">Salida</th>
                    <th className="px-4 py-1 font-medium text-right">Visitas</th>
                    <th className="px-4 py-1 font-medium text-right">Únicos</th>
                    <th className="px-4 py-1 font-medium text-right">Reserve</th>
                    <th className="px-4 py-1 font-medium text-right">Retenidas</th>
                    <th className="px-4 py-1 font-medium text-right">Pagadas</th>
                    <th className="px-4 py-1 font-medium text-right">Conv.</th>
                    <th className="px-4 py-1 font-medium">Plazas</th>
                    <th className="px-4 py-1 font-medium">Origen principal</th>
                    <th className="px-4 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.trips.map((t) => (
                    <tr key={t.curated_trip_id} className="border-t border-clay-100 hover:bg-clay-50" data-testid={`stats-row-${t.curated_trip_id}`}>
                      <td className="px-4 py-2">
                        <button onClick={() => navigate(`/curated-trips/${t.curated_trip_id}?tab=stats`)} className="text-left font-medium text-clay-900 hover:underline">{t.name}</button>
                      </td>
                      <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_KLASS[t.status] || ""}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
                      <td className="px-4 py-2 text-clay-700 whitespace-nowrap">{fmtDate(t.start_date)}</td>
                      <td className="px-4 py-2 text-right tabular font-medium">{t.views}</td>
                      <td className="px-4 py-2 text-right tabular">{t.visitors}</td>
                      <td className="px-4 py-2 text-right tabular">{t.reserve_opens}</td>
                      <td className="px-4 py-2 text-right tabular">{t.holds}</td>
                      <td className="px-4 py-2 text-right tabular">{t.paid}</td>
                      <td className="px-4 py-2 text-right tabular text-clay-700">{t.rates.paid}%</td>
                      <td className="px-4 py-2 tabular text-clay-700 whitespace-nowrap">{t.seats_committed}/{t.capacity}</td>
                      <td className="px-4 py-2">{t.top_source ? <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${SOURCE_KLASS[t.top_source]}`}>{SOURCE_LABEL[t.top_source]}</span> : <span className="text-clay-400">—</span>}</td>
                      <td className="px-4 py-2 text-right">
                        {t.public_token && t.status !== "draft" && (
                          <a href={`/curated/${t.public_token}`} target="_blank" rel="noreferrer" className="inline-flex text-clay-500 hover:text-terracotta" title="Ver landing"><ExternalLink size={12}/></a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <FunnelByDestination funnel={data.funnel}/>

          <DailyChart daily={data.daily}/>
          <div className="grid lg:grid-cols-2 gap-6">
            <SourcesTable sources={data.sources}/>
            <CampaignsTable campaigns={data.campaigns}/>
          </div>
        </div>
      )}
    </div>
  );
}
